// [S-416] Navigable command registry for the ⌘K command palette's "Go to"
// group. Mirrors the role gating in Sidebar.tsx so the palette only offers
// routes the caller can actually reach (the screens themselves + the backend
// still enforce access; this just avoids dead "Go to" entries). Plain data —
// no icons — so the palette can fuzzy-match label + keywords cheaply.
import { sessionCan } from "../auth/capabilities";
import type { WebSession } from "../auth/types";

export type NavCommand = {
  /** Stable id (for React keys + active tracking). */
  id: string;
  /** Visible label, e.g. "Templates". */
  label: string;
  /** Route to navigate to on activate. */
  to: string;
  /** Extra search terms not in the label (synonyms / what the screen does). */
  keywords?: string;
};

export function navCommandsFor(session: WebSession | null): NavCommand[] {
  const cmds: NavCommand[] = [
    { id: "home", label: "Home", to: "/home", keywords: "chat assistant ai agents" },
    { id: "library", label: "Templates", to: "/library", keywords: "templates certified standard" },
  ];

  // Tenant-scoped surfaces — require an own studio (the backend rejects the
  // layer1 namespace for a caller with none), exactly as the sidebar does.
  const hasStudio = session?.studioId != null;
  if (hasStudio) {
    cmds.push(
      { id: "precedent", label: "Precedent", to: "/precedent", keywords: "deals history search prior" },
      { id: "drafts", label: "Draft Workspace", to: "/drafts", keywords: "review contract draft editor" }
    );
  }

  cmds.push(
    {
      id: "attestation",
      label: "Approvals",
      to: sessionCan(session, "reattestation:manage", { layer: "L1" })
        ? "/reattestation"
        : "/attestation",
      keywords: "re-attestation schedule certify",
    },
    { id: "briefs", label: "Pre-negotiation briefs", to: "/briefs", keywords: "negotiation brief" },
    { id: "activity", label: "Activity", to: "/activity", keywords: "audit feed events" },
    { id: "index-health", label: "Search status", to: "/index-health", keywords: "index health status" },
    { id: "upload", label: "Upload portal", to: "/upload", keywords: "ingest documents contracts" }
  );
  if (sessionCan(session, "tenant:manage")) {
    cmds.push({ id: "settings", label: "Settings", to: "/settings", keywords: "roles permissions members config" });
  }

  // Tenant admin surfaces — each gated by the capability its screen needs, so a
  // custom persona only sees what it can actually open.
  if (hasStudio) {
    // No `!` needed — `hasStudio` is an aliased condition, so TS narrows
    // `session` to non-null here. An assertion trips no-unnecessary-type-assertion.
    const studioId = session.studioId;
    if (sessionCan(session, "tenant:manage")) {
      cmds.push({ id: "users", label: "Users", to: `/admin/tenants/tenant-${studioId}/users`, keywords: "members invite roles team" });
    }
    if (sessionCan(session, "template:author")) {
      cmds.push({ id: "template-management", label: "Template Management", to: "/template-management" });
    }
    if (sessionCan(session, "classification:manage")) {
      cmds.push({ id: "tenant-classification", label: "Classification", to: "/settings/classification", keywords: "taxonomy tags" });
    }
    if (sessionCan(session, "review:moderate")) {
      cmds.push({ id: "review-queue", label: "Review queue", to: "/queue", keywords: "ingest approve" });
    }
  }

  // Platform surfaces — platform reach only.
  if (sessionCan(session, "platform:manage_tenants", { layer: "L1" })) {
    cmds.push(
      { id: "tenants", label: "Organizations", to: "/admin/tenants", keywords: "studios provision customers" },
      { id: "attestation-records", label: "Approval records", to: "/attestation" },
      { id: "template-management", label: "Template Management", to: "/template-management" },
      { id: "certification-queue", label: "Certification queue", to: "/certification-queue" },
      { id: "classification", label: "Classification", to: "/classification", keywords: "taxonomy tags" },
      { id: "ingestion", label: "Ingestion status", to: "/ingestion" },
      { id: "review-queue", label: "Review queue", to: "/queue" },
      { id: "atlas", label: "Platform atlas", to: "/atlas", keywords: "map surface" }
    );
  }

  return cmds;
}

/** Case-insensitive match of a query against a command's label + keywords. */
export function commandMatches(cmd: NavCommand, q: string): boolean {
  return matchesWords(`${cmd.label} ${cmd.keywords ?? ""}`, q);
}

/** Every query token must PREFIX a word in the haystack, case-insensitively.
 *
 *  Word-anchored, not free substring: a bare `includes()` matched "omar"
 *  inside "cust-omar-y", which put wildly unrelated rows in the top-bar
 *  results. Anchoring to word starts still supports as-you-type prefixes
 *  ("cust" → "customary", "direct" → "Directors Guild") and stays
 *  order-independent across tokens ("feature lead" matches "Lead — Feature"). */
export function matchesWords(haystack: string, q: string): boolean {
  const tokens = q
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0);
  if (tokens.length === 0) return false;
  const hay = haystack.toLowerCase();
  return tokens.every((t) => hasWordStartingWith(hay, t));
}

// Scan for `token` and accept only occurrences at a word start — i.e. at index
// 0, or preceded by something that isn't a letter/digit. Kept as an indexOf
// walk rather than a RegExp so an arbitrary user query needs no escaping.
function hasWordStartingWith(hay: string, token: string): boolean {
  for (let from = 0; from <= hay.length - token.length; ) {
    const i = hay.indexOf(token, from);
    if (i === -1) return false;
    if (i === 0 || !/[\p{L}\p{N}]/u.test(hay[i - 1] ?? "")) return true;
    from = i + 1;
  }
  return false;
}
